import React from 'react';
import useCategories from '../../hooks/useCategories';

const SearchSuggestions = ({ 
  isOpen, 
  props, 
  onSuggestionClick 
}) => {
  const { rootCategories, loading: categoriesLoading } = useCategories();
  
  if (!isOpen) return null;

  // Enhanced Search Suggestions
  const generateSearchSuggestions = () => {
    const suggestions = {
      categories: {
        title: 'Root Categories',
        icon: '📁',
        items: categoriesLoading ? ['Loading categories...'] : rootCategories
      },
      materials: {
        title: 'Materials',
        icon: '🧱',
        items: [...new Set(props.flatMap(p => p.material || []))].slice(0, 8)
      },
      tags: {
        title: 'Popular Tags',
        icon: '🏷️',
        items: (() => {
          const tagCounts = {};
          props.forEach(p => {
            (p.tags || []).forEach(tag => {
              tagCounts[tag] = (tagCounts[tag] || 0) + 1;
            });
          });
          return Object.entries(tagCounts)
            .sort(([,a], [,b]) => b - a)
            .slice(0, 8)
            .map(([tag]) => tag);
        })()
      },
      periods: {
        title: 'Historical Periods',
        icon: '📅',
        items: [...new Set(props.map(p => p.historical_period).filter(Boolean))]
      },
      quickSearches: {
        title: 'Quick Searches',
        icon: '⚡',
        items: ['furniture', 'vintage', 'lighting', 'decor', 'large items', 'small props']
      }
    };
    
    // Filter out empty groups (but keep loading states)
    return Object.fromEntries(
      Object.entries(suggestions).filter(([key, group]) => 
        group.items.length > 0 || (key === 'categories' && categoriesLoading)
      )
    );
  };

  const handleSuggestionClick = (suggestion, type) => {
    onSuggestionClick(suggestion, type);
  };

  return (
    <div className="absolute top-full left-0 right-0 mt-2 bg-white border border-gray-200 rounded-lg shadow-xl z-50 max-h-96 overflow-y-auto">
      {Object.entries(generateSearchSuggestions()).map(([groupKey, group]) => (
        <div key={groupKey} className="border-b border-gray-100 last:border-b-0">
          {/* Group Header */}
          <div className="px-4 py-2 bg-gray-50 border-b border-gray-100">
            <div className="flex items-center gap-2 text-sm font-medium text-gray-700">
              <span>{group.icon}</span>
              <span>{group.title}</span>
              <span className="text-xs text-gray-500">({group.items.length})</span>
            </div>
          </div>
          
          {/* Group Items */}
          <div className="p-2">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-1">
              {group.items.map((item, index) => (
                <button
                  key={`${groupKey}-${index}`}
                  onClick={() => handleSuggestionClick(item, groupKey)}
                  disabled={categoriesLoading && groupKey === 'categories'}
                  className={`text-left px-3 py-2 rounded-md transition-colors text-sm ${
                    categoriesLoading && groupKey === 'categories'
                      ? 'text-gray-400 cursor-not-allowed'
                      : 'hover:bg-blue-50 hover:text-blue-700'
                  }`}
                >
                  {item}
                </button>
              ))}
            </div>
          </div>
        </div>
      ))}
      
      {/* Footer */}
      <div className="p-3 bg-gray-50 border-t border-gray-100">
        <div className="text-xs text-gray-500 text-center">
          💡 Tip: Click any suggestion to apply it instantly, or use the advanced filters above
        </div>
      </div>
    </div>
  );
};

export default SearchSuggestions;